import { lazy, Suspense, createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  ChevronDown,
  ChevronRight,
  Clock,
  Folder,
  FolderPlus,
  Inbox,
  LayoutDashboard,
  LayoutGrid,
  Layers,
  List,
  LogOut,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Shield,
  Star,
  SquareCheckBig,
  Trash2,
  UserRound,
  X,
  Users,
  FolderInput,
} from 'lucide-react';
import { navigationPrefsOf, type NavigationPrefs, placementPath, type Board, type NavArea, type Navigation } from '@kaba/shared';
import { api } from '../lib/api';
import { SIMPLE, locParam, resolveHomeLocation, type Loc } from '../lib/homeLocation';
import { signOut, useMe, usePublicConfig } from '../lib/auth';
import { queryClient, useBoards } from '../lib/board';
import { accountPref, recentBoardIds, setAccountPref, useToggleFavorite } from '../lib/boardNav';
import { moveBoard, refreshStructure, samePlace, useNav, type Target } from '../lib/structure';
import { FavoriteButton } from './board/BoardSwitcher';
import { navigate, setQuery, useLocation } from '../lib/router';
import { MyCardsList } from './MyCardsPage';
import { DashboardView, useDashboards } from './dashboard/DashboardView';
import { toast } from '../lib/toast';
import { InboxButton } from './Inbox';
import { NewBoardDialog } from './NewBoardDialog';
import { AreaFormDialog, AreaMembersDialog, FolderFormDialog } from './home/AreaDialogs';
import { MoveBoardDialog } from './home/MoveBoardDialog';
import { Avatar, Button, cx, DropdownContent, DropdownItem, DropdownMenu, DropdownTrigger, IconButton, Sheet } from './ui/ui';

/* ---------- Ziehen auf Bereiche und Ordner: nur eine Abkürzung für „Verschieben nach …“ (B02) ---------- */

const DragCtx = createContext<Board | null>(null);
let lastDrop = 0;

function dropTarget(id: string): Target | null {
  if (id === 'drop:none') return { areaId: null, folderId: null };
  const m = /^drop:(area|folder):([^:]+):?(.*)$/.exec(id);
  if (!m) return null;
  return m[1] === 'area' ? { areaId: m[2]!, folderId: null } : { areaId: m[3]!, folderId: m[2]! };
}

/** Ziel in der Navigation: nur hervorgehoben, wenn das gezogene Board dorthin darf */
function DropZone({ id, teamId, target, children }: { id: string; teamId: string | null; target: Target; children: ReactNode }) {
  const dragging = useContext(DragCtx);
  const allowed = !!dragging && (target.areaId === null || dragging.teamId === teamId) && !samePlace(dragging.placement, target);
  const { setNodeRef, isOver } = useDroppable({ id, disabled: !allowed });
  return (
    <div
      ref={setNodeRef}
      className={cx('flex min-w-0 flex-1 rounded-lg', allowed && 'ring-1 ring-accent/50 ring-dashed', allowed && isOver && 'bg-accent-soft ring-2 ring-accent')}
    >
      {children}
    </div>
  );
}

/* ---------- Orte ---------- */

const PlanningView=lazy(()=>import('./planning/PlanningView'));
const sameLoc = (a: Loc, b: Loc) => locParam(a) === locParam(b);

const LABELS: Record<(typeof SIMPLE)[number], string> = {
  uebersicht: 'Übersicht',
  planung: 'Tagesplanung',
  favoriten: 'Favoriten',
  zuletzt: 'Zuletzt geöffnet',
  alle: 'Alle Boards',
  meine: 'Meine Karten',
  unsortiert: 'Nicht einsortiert',
  archiv: 'Archiv',
};

export function goToPlace(loc: string) {
  navigate(`/?ort=${loc}`);
}

/** Übersicht: Navigation (Favoriten, Zuletzt, Alle, Meine Karten, Bereiche, Nicht einsortiert, Archiv) und Inhalt (N01) */
import { BrandLogo } from './BrandLogo';

export function BoardsPage() {
  const { data: boards, isLoading } = useBoards();
  const { data: me } = useMe();
  const { data: cfg } = usePublicConfig();
  const navQuery = useNav(!!me && !me.isGuest);
  const nav = navQuery.data;
  const { query } = useLocation();
  const [create, setCreate] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => accountPref('navCollapsed', false));

  const navigationPrefs = navigationPrefsOf(me?.navigationPrefs);
  const dashList = useDashboards(!!me && !me.isGuest);
  const { loc, startDash } = resolveHomeLocation({
    explicit: query.get('ort'), legacyMine: query.get('tab') === 'mine',
    startDashboardId: !me?.isGuest ? dashList.data?.prefs.startDashboardId ?? null : null,
    lastPlace: accountPref<string | null>('lastPlace', null), prefs: navigationPrefs,
  });
  const dashId = query.get('d') ?? startDash;
  useEffect(() => setAccountPref('lastPlace', locParam(loc)), [loc]);
  useEffect(() => setAccountPref('navCollapsed', collapsed), [collapsed]);

  const go = (l: Loc) => {
    setQuery({ ort: locParam(l), tab: null, q: null, d: null }, { replace: false });
    setNavOpen(false);
  };

  const area =
    loc.kind === 'bereich'
      ? nav?.areas.find((a) => a.id === loc.id)
      : loc.kind === 'ordner'
        ? nav?.areas.find((a) => a.folders.some((f) => f.id === loc.id))
        : undefined;
  const folder = loc.kind === 'ordner' ? area?.folders.find((f) => f.id === loc.id) : undefined;
  // Ort existiert nicht (mehr) oder ist nicht sichtbar: zurück zu „Alle Boards“
  useEffect(() => {
    if (nav && !navQuery.isFetching && (loc.kind === 'bereich' || loc.kind === 'ordner') && !area) {
      toast.info('Diesen Bereich bzw. Ordner gibt es nicht mehr oder du gehörst nicht mehr dazu.');
      setQuery({ ort: 'alle' }, { replace: true });
    }
  }, [nav, navQuery.isFetching, loc, area]);

  const placeName = loc.kind === 'bereich' ? (area?.name ?? 'Bereich') : loc.kind === 'ordner' ? (folder?.name ?? 'Ordner') : LABELS[loc.kind];
  const target =
    loc.kind === 'bereich' && area
      ? { areaId: area.id, folderId: null }
      : loc.kind === 'ordner' && area && folder
        ? { areaId: area.id, folderId: folder.id }
        : null;
  const canCreate = !me?.isGuest && loc.kind !== 'archiv' && loc.kind !== 'meine' && loc.kind !== 'uebersicht';
  // Gäste haben keine Übersicht
  useEffect(() => {
    if (me?.isGuest && loc.kind === 'uebersicht') setQuery({ ort: 'alle' }, { replace: true });
  }, [me?.isGuest, loc.kind]);

  // Maus: ab 6 px Bewegung; Touch: erst nach längerem Halten, damit Wischen weiter scrollt (B02)
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 6 } }),
  );
  const [dragging, setDragging] = useState<Board | null>(null);
  const onDragEnd = (e: DragEndEvent) => {
    const b = dragging;
    setDragging(null);
    lastDrop = Date.now();
    const t = e.over ? dropTarget(String(e.over.id)) : null;
    if (b && t) void moveBoard(b, t);
  };

  const navPanel = <PlacesNav prefs={navigationPrefs} nav={nav} boards={boards} loc={loc} onGo={go} isGuest={!!me?.isGuest} onAreaCreated={(id) => go({ kind: 'bereich', id })} />;

  return (
    <div className="mx-auto flex min-h-full max-w-6xl flex-col px-4">
      <header className="pt-safe flex items-center gap-2 py-3">
        {cfg?.branding?.logo ? (
          <BrandLogo logo={cfg.branding.logo} name={`Logo von ${cfg.instanceName}`} />
        ) : (
          <span className="inline-flex size-9 items-center justify-center rounded-xl bg-accent text-on-accent">
            <LayoutGrid size={20} />
          </span>
        )}
        <h1 className="flex-1 truncate text-xl font-bold">{cfg?.instanceName ?? 'KaBa'}</h1>
        <InboxButton />
        {me && (
          <DropdownMenu>
            <DropdownTrigger asChild>
              <button aria-label="Konto-Menü" className="inline-flex size-11 items-center justify-center rounded-full">
                <Avatar user={me} size={32} />
              </button>
            </DropdownTrigger>
            <DropdownContent>
              <div className="px-2.5 py-2 text-xs text-[var(--ink-soft)]">
                <span className="block font-medium text-[var(--ink)]">{me.name}</span>
                {me.isGuest ? 'Gastzugang' : me.email}
              </div>
              <DropdownItem icon={<UserRound size={16} />} onSelect={() => navigate('/profil')}>
                {me.isGuest ? 'Name ändern' : 'Profil & Benachrichtigungen'}
              </DropdownItem>
              {!me.isGuest && (
                <DropdownItem icon={<Users size={16} />} onSelect={() => navigate('/teams')}>
                  Teams
                </DropdownItem>
              )}
              {me.isAdmin && (
                <DropdownItem icon={<Shield size={16} />} onSelect={() => navigate('/admin')}>
                  Administration
                </DropdownItem>
              )}
              <DropdownItem icon={<LogOut size={16} />} onSelect={() => void signOut()}>
                Abmelden
              </DropdownItem>
            </DropdownContent>
          </DropdownMenu>
        )}
      </header>

      {/* Handy: beschrifteter Knopf statt schmaler Baumspalte (M01) */}
      <div className="mb-2 md:hidden">
        <Button onClick={() => setNavOpen(true)} className="w-full justify-start" aria-haspopup="dialog">
          <Layers size={17} aria-hidden /> <span className="font-semibold">Boards</span>
          <span className="min-w-0 truncate text-[var(--ink-soft)]">· {area && folder ? `${area.name} / ${folder.name}` : placeName}</span>
          <ChevronDown size={16} className="ml-auto" aria-hidden />
        </Button>
      </div>
      <Sheet open={navOpen} onOpenChange={setNavOpen} title="Boards" side="center">
        <div className="p-2">{navPanel}</div>
      </Sheet>

      <DndContext
        sensors={sensors}
        onDragStart={(e) => setDragging((boards ?? []).find((b) => `drag:${b.id}` === e.active.id) ?? null)}
        onDragCancel={() => setDragging(null)}
        onDragEnd={onDragEnd}
      >
        <DragCtx.Provider value={dragging}>
          <div className={cx('flex flex-1 gap-6 pb-10', !collapsed && 'md:grid md:grid-cols-[15rem_minmax(0,1fr)]')}>
            {!collapsed && (
              <aside className="hidden md:block">
                <div className="sticky top-2 flex max-h-[calc(100dvh-1rem)] flex-col overflow-y-auto">
                  {navPanel}
                  <button
                    type="button"
                    onClick={() => setCollapsed(true)}
                    className="mt-2 inline-flex min-h-10 items-center gap-2 rounded-lg px-2 text-sm text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10"
                  >
                    <PanelLeftClose size={16} aria-hidden /> Navigation einklappen
                  </button>
                </div>
              </aside>
            )}
            <main className="min-w-0 flex-1">
              {collapsed && (
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  className="mb-2 hidden min-h-10 items-center gap-2 rounded-lg px-2 text-sm text-[var(--ink-soft)] hover:bg-black/5 md:inline-flex dark:hover:bg-white/10"
                >
                  <PanelLeftOpen size={16} aria-hidden /> Navigation einblenden
                </button>
              )}
              {loc.kind !== 'uebersicht' && (
                <PlaceHeader loc={loc} nav={nav} area={area} folder={folder} name={placeName} canCreate={canCreate} onCreate={() => setCreate(true)} onGo={go} />
              )}
              {loc.kind === 'alle' && !me?.isGuest && navigationPrefs.showOverview && <DashboardHint onOpen={() => go({ kind: 'uebersicht' })} />}
              {loc.kind === 'planung' && !me?.isGuest ? (<Suspense fallback={<p>Tagesplanung wird geladen …</p>}><PlanningView /></Suspense>) : loc.kind === 'uebersicht' && !me?.isGuest ? (
                <DashboardView selectedId={dashId} onSelect={(id) => setQuery({ ort: 'uebersicht', d: id })} />
              ) : loc.kind === 'meine' ? (
                <MyCardsList />
              ) : (
                <PlaceContent
                  loc={loc}
                  boards={boards}
                  loading={isLoading}
                  nav={nav}
                  area={area}
                  folder={folder}
                  onGo={go}
                  isGuest={!!me?.isGuest}
                  onCreate={() => setCreate(true)}
                />
              )}
            </main>
          </div>
        </DragCtx.Provider>
        <DragOverlay dropAnimation={null}>
          {dragging && <div className="rounded-xl border border-accent bg-[var(--panel)] px-4 py-3 text-sm font-semibold shadow-xl">{dragging.title}</div>}
        </DragOverlay>
      </DndContext>

      <NewBoardDialog
        open={create}
        target={target}
        onClose={() => setCreate(false)}
      />
    </div>
  );
}

/* ---------- Navigation ---------- */

export function PlacesNav({
  prefs,
  nav,
  boards,
  loc,
  onGo,
  isGuest,
  onAreaCreated,
}: {
  prefs: NavigationPrefs;
  nav: Navigation | undefined;
  boards: Board[] | undefined;
  loc: Loc;
  onGo: (l: Loc) => void;
  isGuest: boolean;
  onAreaCreated: (id: string) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(() => accountPref('areasOpen', {}));
  const [newArea, setNewArea] = useState(false);
  useEffect(() => setAccountPref('areasOpen', open), [open]);
  const count = (f: (b: Board) => boolean) => boards?.filter(f).length;
  const item = (l: Loc, label: string, icon: ReactNode, n?: number, indent = false) => {
    const active = sameLoc(l, loc);
    return (
      <a
        href={`/?ort=${locParam(l)}`}
        onClick={(e) => {
          e.preventDefault();
          onGo(l);
        }}
        aria-current={active ? 'page' : undefined}
        className={cx(
          'flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-sm',
          indent && 'pl-8',
          active ? 'bg-accent-soft font-semibold text-[var(--ink)]' : 'hover:bg-black/5 dark:hover:bg-white/10',
        )}
      >
        <span className="shrink-0 text-[var(--ink-soft)]">{icon}</span>
        <span className="min-w-0 flex-1 break-words">{label}</span>
        {n !== undefined && <span className="shrink-0 text-xs text-[var(--ink-soft)]">{n}</span>}
      </a>
    );
  };
  return (
    <nav aria-label="Boards-Navigation" className="flex flex-col gap-0.5">
      {item(
        { kind: 'favoriten' },
        'Favoriten',
        <Star size={16} />,
        count((b) => !!b.favorite),
      )}
      {item({ kind: 'zuletzt' }, 'Zuletzt geöffnet', <Clock size={16} />)}
      {item({ kind: 'alle' }, 'Alle Boards', <LayoutGrid size={16} />, boards?.length)}
      <div className="mt-3 mb-1 border-t border-[var(--line)] px-2 pt-3">
        <h2 className="text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">Persönlich</h2>
      </div>
      {item({ kind: 'meine' }, 'Meine Karten', <SquareCheckBig size={16} />)}
      {!isGuest && prefs.showOverview && item({ kind: 'uebersicht' }, 'Übersicht', <LayoutDashboard size={16} />)}
      {!isGuest && prefs.showPlanning && item({ kind: 'planung' }, 'Tagesplanung', <Clock size={16} />)}
      {!isGuest && (
        <>
          <div className="mt-3 mb-1 flex items-center gap-1 border-t border-[var(--line)] px-2 pt-3">
            <h2 className="flex-1 text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">Bereiche</h2>
            {!!nav?.teams.length && (
              <IconButton label="Neuer Bereich" onClick={() => setNewArea(true)} className="size-9">
                <Plus size={16} />
              </IconButton>
            )}
          </div>
          {nav?.areas.length === 0 && (
            <p className="px-2 pb-1 text-xs text-[var(--ink-soft)]">
              {nav.teams.length ? 'Noch keine Bereiche. Bereiche ordnen Boards eines Teams, z. B. „Kundenprojekte“.' : 'Bereiche gibt es innerhalb von Teams.'}
            </p>
          )}
          <ul className="flex flex-col gap-0.5">
            {nav?.areas.map((a) => {
              const expanded = open[a.id] ?? (loc.kind === 'ordner' && a.folders.some((f) => f.id === loc.id));
              const dup = nav.areas.filter((x) => x.name === a.name).length > 1;
              return (
                <li key={a.id}>
                  <div className="flex items-center">
                    {a.folders.length > 0 ? (
                      <button
                        type="button"
                        aria-expanded={!!expanded}
                        aria-label={`Ordner von ${a.name} ${expanded ? 'einklappen' : 'ausklappen'}`}
                        onClick={() => setOpen((o) => ({ ...o, [a.id]: !expanded }))}
                        className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      </button>
                    ) : (
                      <span className="size-8 shrink-0" />
                    )}
                    <DropZone id={`drop:area:${a.id}`} teamId={a.teamId} target={{ areaId: a.id, folderId: null }}>
                      {item(
                        { kind: 'bereich', id: a.id },
                        dup ? `${a.name} (${a.teamName})` : a.name,
                        <Layers size={16} />,
                        a.boardCount + a.folders.reduce((s, f) => s + f.boardCount, 0),
                      )}
                    </DropZone>
                  </div>
                  {expanded && (
                    <ul className="flex flex-col gap-0.5">
                      {a.folders.map((f) => (
                        <li key={f.id} className="flex">
                          <DropZone id={`drop:folder:${f.id}:${a.id}`} teamId={a.teamId} target={{ areaId: a.id, folderId: f.id }}>
                            {item({ kind: 'ordner', id: f.id }, f.name, <Folder size={15} />, f.boardCount, true)}
                          </DropZone>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div className="mt-4 flex flex-col gap-0.5">
        <DropZone id="drop:none" teamId={null} target={{ areaId: null, folderId: null }}>
          {item(
            { kind: 'unsortiert' },
            'Nicht einsortiert',
            <Inbox size={16} />,
            count((b) => b.placement?.kind === 'none'),
          )}
        </DropZone>
        {item({ kind: 'archiv' }, 'Archiv', <Archive size={16} />)}
      </div>
      <AreaFormDialog open={newArea} onClose={() => setNewArea(false)} onCreated={onAreaCreated} />
    </nav>
  );
}

/* ---------- Kopf des Inhalts: Pfad, Name, Beschreibung, Aktionen ---------- */

function PlaceHeader({
  loc,
  area,
  folder,
  name,
  canCreate,
  onCreate,
  onGo,
}: {
  loc: Loc;
  nav: Navigation | undefined;
  area: NavArea | undefined;
  folder: { id: string; name: string } | undefined;
  name: string;
  canCreate: boolean;
  onCreate: () => void;
  onGo: (l: Loc) => void;
}) {
  const [dialog, setDialog] = useState<null | 'edit' | 'members' | 'folder' | 'rename-folder'>(null);
  const removeArea = async () => {
    if (!area || !window.confirm(`Bereich „${area.name}“ entfernen? Das geht nur, wenn er keine Ordner und keine Boards mehr enthält.`)) return;
    try {
      await api.del(`/api/areas/${area.id}`);
      refreshStructure();
      onGo({ kind: 'alle' });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const removeFolder = async () => {
    if (!folder || !area || !window.confirm(`Ordner „${folder.name}“ entfernen? Das geht nur, wenn er keine Boards mehr enthält – auch keine archivierten.`))
      return;
    try {
      await api.del(`/api/folders/${folder.id}`);
      refreshStructure();
      onGo({ kind: 'bereich', id: area.id });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  return (
    <div className="mb-3 flex flex-col gap-1">
      {loc.kind === 'ordner' && area && (
        <nav aria-label="Pfad" className="text-sm text-[var(--ink-soft)]">
          <a
            href={`/?ort=bereich-${area.id}`}
            onClick={(e) => {
              e.preventDefault();
              onGo({ kind: 'bereich', id: area.id });
            }}
            className="underline-offset-2 hover:underline"
          >
            {area.name}
          </a>{' '}
          /
        </nav>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex min-w-0 flex-1 items-center gap-2 text-lg font-bold break-words">
          {loc.kind === 'bereich' ? (
            <Layers size={19} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
          ) : loc.kind === 'ordner' ? (
            <Folder size={19} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
          ) : null}
          {name}
        </h2>
        {(loc.kind === 'bereich' || loc.kind === 'ordner') && area && (
          <DropdownMenu>
            <DropdownTrigger asChild>
              <IconButton label={loc.kind === 'bereich' ? `Menü des Bereichs ${area.name}` : `Menü des Ordners ${folder?.name ?? ''}`}>
                <MoreHorizontal size={18} />
              </IconButton>
            </DropdownTrigger>
            <DropdownContent>
              {loc.kind === 'bereich' && area.canManage && (
                <>
                  <DropdownItem icon={<FolderPlus size={16} />} onSelect={() => setDialog('folder')}>
                    Neuer Ordner
                  </DropdownItem>
                  <DropdownItem icon={<Pencil size={16} />} onSelect={() => setDialog('edit')}>
                    Bereich bearbeiten
                  </DropdownItem>
                </>
              )}
              {loc.kind === 'ordner' && area.canManage && (
                <DropdownItem icon={<Pencil size={16} />} onSelect={() => setDialog('rename-folder')}>
                  Ordner umbenennen
                </DropdownItem>
              )}
              <DropdownItem icon={<Users size={16} />} onSelect={() => setDialog('members')}>
                Mitglieder des Bereichs
              </DropdownItem>
              {loc.kind === 'bereich' && area.canManage && (
                <DropdownItem danger icon={<Trash2 size={16} />} onSelect={() => void removeArea()}>
                  Bereich entfernen
                </DropdownItem>
              )}
              {loc.kind === 'ordner' && area.canManage && (
                <DropdownItem danger icon={<Trash2 size={16} />} onSelect={() => void removeFolder()}>
                  Ordner entfernen
                </DropdownItem>
              )}
            </DropdownContent>
          </DropdownMenu>
        )}
        {canCreate && (
          <Button variant="primary" onClick={onCreate}>
            <Plus size={17} aria-hidden /> <span className="hidden sm:inline">Neues Board</span>
            <span className="sm:hidden">Neu</span>
          </Button>
        )}
      </div>
      {loc.kind === 'bereich' && area?.description && <p className="text-sm text-[var(--ink-soft)]">{area.description}</p>}
      {area && (
        <>
          <AreaFormDialog area={area} open={dialog === 'edit'} onClose={() => setDialog(null)} />
          <FolderFormDialog areaId={area.id} open={dialog === 'folder'} onClose={() => setDialog(null)} onCreated={(id) => onGo({ kind: 'ordner', id })} />
          <FolderFormDialog areaId={area.id} folder={folder} open={dialog === 'rename-folder'} onClose={() => setDialog(null)} />
          <AreaMembersDialog area={area} open={dialog === 'members'} onClose={() => setDialog(null)} onLeft={() => onGo({ kind: 'alle' })} />
        </>
      )}
    </div>
  );
}

/* ---------- Inhalt ---------- */

type SortBy = 'name' | 'changed';

function PlaceContent({
  loc,
  boards,
  loading,
  area,
  folder,
  onGo,
  isGuest,
  onCreate,
}: {
  loc: Loc;
  boards: Board[] | undefined;
  loading: boolean;
  nav: Navigation | undefined;
  area: NavArea | undefined;
  folder: { id: string; name: string } | undefined;
  onGo: (l: Loc) => void;
  isGuest: boolean;
  onCreate: () => void;
}) {
  const { query } = useLocation();
  const q = query.get('q') ?? '';
  const [everywhere, setEverywhere] = useState(false);
  const [mode, setMode] = useState<'list' | 'tiles'>(() => accountPref('boardMode', 'tiles'));
  const [sort, setSort] = useState<SortBy>(() => accountPref('boardSort', 'name'));
  const [moving, setMoving] = useState<string | null>(null);
  const fav = useToggleFavorite();
  useEffect(() => setAccountPref('boardMode', mode), [mode]);
  useEffect(() => setAccountPref('boardSort', sort), [sort]);
  useEffect(() => setEverywhere(false), [loc.kind, 'id' in loc ? loc.id : '']);
  const archived = useQuery({ queryKey: ['boards-archived'], queryFn: () => api.get<Board[]>('/api/boards?archived=1'), enabled: loc.kind === 'archiv' });

  const term = q.trim().toLowerCase();
  const scoped = loc.kind !== 'alle' && loc.kind !== 'archiv';
  const searchAll = !!term && (everywhere || !scoped);

  const list = useMemo(() => {
    const all = loc.kind === 'archiv' ? (archived.data ?? []) : (boards ?? []);
    let shown: Board[];
    if (searchAll && loc.kind !== 'archiv') shown = boards ?? [];
    else if (loc.kind === 'favoriten') shown = all.filter((b) => b.favorite);
    else if (loc.kind === 'zuletzt') {
      const byId = new Map(all.map((b) => [b.id, b]));
      shown = recentBoardIds()
        .map((id) => byId.get(id))
        .filter((b): b is Board => !!b);
    } else if (loc.kind === 'unsortiert') shown = all.filter((b) => b.placement?.kind === 'none');
    else if (loc.kind === 'bereich') shown = all.filter((b) => b.placement?.kind === 'visible' && b.placement.areaId === loc.id && !b.placement.folderId);
    else if (loc.kind === 'ordner') shown = all.filter((b) => b.placement?.kind === 'visible' && b.placement.folderId === loc.id);
    else shown = all;
    if (term) shown = shown.filter((b) => `${b.title} ${b.placement?.kind === 'visible' ? placementPath(b.placement) : ''}`.toLowerCase().includes(term));
    if (loc.kind !== 'zuletzt' || term) {
      shown = [...shown].sort((a, b) => (sort === 'name' ? a.title.localeCompare(b.title, 'de') : b.updatedAt.localeCompare(a.updatedAt)));
    }
    return shown;
  }, [boards, archived.data, loc, term, searchAll, sort]);

  // Pfad nur dort, wo er etwas sagt (globale Listen, Suche über alles)
  const showPath = searchAll || ['favoriten', 'zuletzt', 'alle', 'archiv'].includes(loc.kind);
  const foldersHere = loc.kind === 'bereich' && area && !searchAll ? area.folders.filter((f) => !term || f.name.toLowerCase().includes(term)) : [];
  const movingBoard = moving ? ((boards ?? []).find((b) => b.id === moving) ?? null) : null;
  const isLoading = loc.kind === 'archiv' ? archived.isLoading : loading;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-0 flex-1 basis-56">
          <span className="sr-only">Boards suchen</span>
          <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-[var(--ink-soft)]" aria-hidden />
          <input
            type="search"
            value={q}
            onChange={(e) => setQuery({ q: e.target.value || null }, { replace: true })}
            placeholder={
              scoped
                ? `In „${loc.kind === 'ordner' ? folder?.name : loc.kind === 'bereich' ? area?.name : LABELS[loc.kind as (typeof SIMPLE)[number]]}“ suchen …`
                : 'Boards suchen …'
            }
            className="min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] pr-2 pl-9 text-base sm:min-h-10 sm:text-sm"
          />
        </label>
        <div role="group" aria-label="Darstellung" className="flex rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
          {(
            [
              ['list', 'Liste', List],
              ['tiles', 'Kacheln', LayoutGrid],
            ] as const
          ).map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              aria-pressed={mode === k}
              onClick={() => setMode(k)}
              className={cx(
                'inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-sm',
                mode === k ? 'bg-[var(--panel)] font-semibold shadow-sm' : 'text-[var(--ink-soft)]',
              )}
            >
              <Icon size={15} aria-hidden /> <span className="max-sm:sr-only">{label}</span>
            </button>
          ))}
        </div>
        {loc.kind !== 'zuletzt' && (
          <label className="text-sm">
            <span className="sr-only">Sortierung</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortBy)}
              className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 sm:min-h-10"
            >
              <option value="name">Nach Name</option>
              <option value="changed">Zuletzt geändert</option>
            </select>
          </label>
        )}
      </div>
      {term && scoped && (
        <label className="flex min-h-10 items-center gap-2 self-start text-sm">
          <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={everywhere} onChange={(e) => setEverywhere(e.target.checked)} />
          Alle Boards durchsuchen
        </label>
      )}

      {loc.kind === 'archiv' && (
        <p className="text-sm text-[var(--ink-soft)]">Archivierte Boards behalten Ablage, Freigaben und Favoriten. Besitzer können sie wiederherstellen.</p>
      )}

      {foldersHere.length > 0 && (
        <ul aria-label="Ordner" className="flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]">
          {foldersHere.map((f) => (
            <li key={f.id}>
              <a
                href={`/?ort=ordner-${f.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  onGo({ kind: 'ordner', id: f.id });
                }}
                className="flex min-h-12 items-center gap-3 px-4 hover:bg-black/[0.03] dark:hover:bg-white/5"
              >
                <Folder size={17} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
                <span className="min-w-0 flex-1 font-medium break-words">{f.name}</span>
                <span className="text-xs text-[var(--ink-soft)]">{f.boardCount === 1 ? '1 Board' : `${f.boardCount} Boards`}</span>
                <ChevronRight size={16} className="text-[var(--ink-soft)]" aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      )}

      {isLoading && <p className="text-sm text-[var(--ink-soft)]">Lade …</p>}
      {!isLoading && list.length === 0 && foldersHere.length === 0 && (
        <EmptyPlace loc={loc} term={term} isGuest={isGuest} total={boards?.length ?? 0} onCreate={onCreate} />
      )}
      {list.length > 0 && (
        <ul
          aria-label={term ? 'Treffer' : 'Boards'}
          className={cx(
            mode === 'tiles'
              ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'
              : 'flex flex-col divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)] bg-[var(--panel)]',
          )}
        >
          {list.map((b) => (
            <BoardItem
              key={b.id}
              board={b}
              mode={mode}
              showPath={showPath}
              onFavorite={(f) => fav.mutate({ id: b.id, favorite: f })}
              onMove={() => setMoving(b.id)}
            />
          ))}
        </ul>
      )}
      {loc.kind !== 'archiv' && (loc.kind === 'bereich' || loc.kind === 'ordner') && list.length > 0 && !term && (
        <p className="text-xs text-[var(--ink-soft)]">{list.length === 1 ? '1 für dich sichtbares Board' : `${list.length} für dich sichtbare Boards`}</p>
      )}
      <MoveBoardDialog board={movingBoard} onClose={() => setMoving(null)} />
    </div>
  );
}

function EmptyPlace({ loc, term, isGuest, total, onCreate }: { loc: Loc; term: string; isGuest: boolean; total: number; onCreate: () => void }) {
  let text = 'Hier sind keine für dich sichtbaren Boards.';
  if (term) text = 'Keine Treffer.';
  else if (isGuest && total === 0) text = 'Du bist als Gast angemeldet und hast gerade keinen Zugang zu einem Board. Öffne den Gastlink, den du bekommen hast.';
  else if (loc.kind === 'favoriten') text = 'Noch keine Favoriten. Mit dem Stern an einem Board merkst du es dir hier – auf allen deinen Geräten.';
  else if (loc.kind === 'zuletzt') text = 'Auf diesem Gerät hast du noch kein Board geöffnet.';
  else if (loc.kind === 'archiv') text = 'Keine archivierten Boards.';
  else if (loc.kind === 'alle' && total === 0) text = 'Noch kein Board. Leg dein erstes an – es startet mit vier Spalten.';
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-8 text-center">
      <p className="text-sm text-[var(--ink-soft)]">{text}</p>
      {!term && !isGuest && loc.kind === 'alle' && total === 0 && (
        <Button variant="primary" onClick={onCreate} className="mt-3">
          <Plus size={17} aria-hidden /> Board anlegen
        </Button>
      )}
    </div>
  );
}

function BoardItem({
  board: b,
  mode,
  showPath,
  onFavorite,
  onMove,
}: {
  board: Board;
  mode: 'list' | 'tiles';
  showPath: boolean;
  onFavorite: (f: boolean) => void;
  onMove: () => void;
}) {
  const owner = b.role === 'owner';
  const archived = !!b.archivedAt;
  const link = useRef<HTMLAnchorElement>(null);
  const drag = useDraggable({ id: `drag:${b.id}`, disabled: !owner || archived });
  const dragProps =
    owner && !archived
      ? { ...drag.listeners, ...drag.attributes, tabIndex: undefined, role: undefined, 'aria-roledescription': undefined, 'aria-describedby': undefined }
      : {};
  const path = b.placement?.kind === 'hidden' ? 'Freigegebenes Board' : placementPath(b.placement);
  // Teamname nur für Teammitglieder (kommt vom Server), hilft bei mehreren Teams
  const meta = [showPath && b.teamName ? b.teamName : null, showPath ? path : null, `Zuletzt geändert ${new Date(b.updatedAt).toLocaleDateString('de-DE')}`].filter(Boolean).join(' · ');
  const archive = async () => {
    if (
      !window.confirm(
        `„${b.title}“ archivieren? Es verschwindet aus den normalen Listen und bleibt unter „Archiv“ mit Ablage, Freigaben und Favoriten erhalten.`,
      )
    )
      return;
    try {
      await api.del(`/api/boards/${b.id}`);
      refreshStructure();
      toast.info(`„${b.title}“ ist archiviert.`, { label: 'Rückgängig', run: () => void restore() });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const restore = async () => {
    try {
      await api.post(`/api/boards/${b.id}/restore`);
      refreshStructure();
      void queryClient.invalidateQueries({ queryKey: ['boards-archived'] });
      toast.info(`„${b.title}“ ist wiederhergestellt.`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };
  const menu = (
    <DropdownMenu>
      <DropdownTrigger asChild>
        <IconButton label={`Aktionen für „${b.title}“`}>
          <MoreHorizontal size={18} />
        </IconButton>
      </DropdownTrigger>
      <DropdownContent>
        {archived ? (
          owner && (
            <DropdownItem icon={<RotateCcw size={16} />} onSelect={() => void restore()}>
              Wiederherstellen
            </DropdownItem>
          )
        ) : (
          <>
            {owner && (
              <DropdownItem icon={<FolderInput size={16} />} onSelect={onMove}>
                Verschieben nach …
              </DropdownItem>
            )}
            {owner && (
              <DropdownItem icon={<Archive size={16} />} onSelect={() => void archive()}>
                Archivieren
              </DropdownItem>
            )}
          </>
        )}
        {!owner && <div className="px-2.5 py-2 text-xs text-[var(--ink-soft)]">Ablage und Archiv ändern die Besitzer.</div>}
      </DropdownContent>
    </DropdownMenu>
  );
  // Kleines Erkennungszeichen aus Team- bzw. Boardgestaltung (V04: kein Fototeppich)
  const mark = b.brand?.mark ?? b.brand?.logo;
  const brandMark = mark ? <BrandLogo logo={{ main: mark, dark: null, mark: null, ratio: b.brand?.mark ? 1 : (b.brand?.ratio ?? 1), markRatio: null }} name="" size="tile" className="shrink-0" /> : null;
  const open = (e: React.MouseEvent) => {
    e.preventDefault();
    // Nach dem Ablegen löst der Browser noch einen Klick aus – der öffnet das Board nicht
    if (Date.now() - lastDrop < 400) return;
    navigate(`/b/${b.id}`);
  };
  if (mode === 'list')
    return (
      <li ref={drag.setNodeRef} {...dragProps} className={cx('flex items-center gap-1 pr-1', drag.isDragging && 'opacity-40')}>
        <a
          ref={link}
          draggable={false}
          href={`/b/${b.id}`}
          onClick={open}
          className="flex min-h-14 min-w-0 flex-1 flex-col justify-center px-4 py-2 hover:bg-black/[0.03] dark:hover:bg-white/5"
        >
          <span className="flex items-center gap-2 font-medium break-words">
            {brandMark}
            {b.title}
          </span>
          <span className="text-xs text-[var(--ink-soft)]">{archived ? `Archiviert · ${path}` : meta}</span>
        </a>
        {!archived && <FavoriteButton board={b} onToggle={onFavorite} />}
        {menu}
      </li>
    );
  return (
    <li ref={drag.setNodeRef} {...dragProps} className={cx('relative', drag.isDragging && 'opacity-40')}>
      <a
        ref={link}
        draggable={false}
        href={`/b/${b.id}`}
        onClick={open}
        className="flex min-h-24 flex-col justify-between gap-2 rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-4 pr-24 shadow-sm hover:border-accent"
        style={b.brand?.accent ? { borderLeft: `4px solid ${b.brand.accent}` } : undefined}
      >
        <span className="flex items-center gap-2 font-semibold break-words">
          {brandMark}
          {b.title}
        </span>
        <span className="text-xs text-[var(--ink-soft)]">{archived ? `Archiviert · ${path}` : meta}</span>
      </a>
      <div className="absolute top-2 right-2 flex">
        {!archived && <FavoriteButton board={b} onToggle={onFavorite} />}
        {menu}
      </div>
    </li>
  );
}

/** Einführung ohne Startseitenwechsel: einmaliger Hinweis auf die neue Übersicht */
function DashboardHint({ onOpen }: { onOpen: () => void }) {
  const [hidden, setHidden] = useState(() => accountPref('dashHintDone', false));
  if (hidden) return null;
  const close = () => {
    setAccountPref('dashHintDone', true);
    setHidden(true);
  };
  return (
    <div className="mb-3 flex items-start gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 text-sm">
      <LayoutDashboard size={18} className="mt-0.5 shrink-0 text-accent-fg" aria-hidden />
      <p className="min-w-0 flex-1">
        <span className="font-semibold">Neu: deine Übersicht.</span> Fristen, nächste Aufgaben, Blockaden und Favoriten auf einen Blick – über alle deine Boards.{' '}
        <button
          type="button"
          className="font-medium text-accent-fg underline"
          onClick={() => {
            close();
            onOpen();
          }}
        >
          Übersicht öffnen
        </button>
      </p>
      <IconButton label="Hinweis ausblenden" onClick={close}>
        <X size={16} />
      </IconButton>
    </div>
  );
}
